import store from '@/store';
import router from '@/router';
import Vue from 'vue';
import businessDateFormats from '@/modules/common/filters/BusinessDateFormats';
import handleNavigation from '@/utils/navigationUtils';
import { getNestedStoreData } from '@/utils/storeUtils';
import { markWebVitalsReady } from '@/plugins/webVitals';
import { helpDestination, openHelp } from '@/modules/help/services/helpEntryService';
import eventListenerService from './eventListenerService';
import outgoingPostMessageUtils from './outgoingPostMessageUtils';
import logError from '../logError/logError';
import { isSameHostname, isSameOrigin } from './postMessageSecurityUtils';

// const iframeId = 'angular-iframe';

let isAngularEntryRoute = false;

// This is the final step in the navigation process from Angular's side
// after we decided whether to ignore angular's navigation or not (due to landing page routing).
// It handles query params and navigation state cleanup.
// It is called from handleAngularNavigation.
const continueAngularNavigation = (path, queryString, isComingFromAngular, isGoingToAngular) => {
  const urlSearchParams = new URLSearchParams(queryString);
  const queryObj = Object.fromEntries(urlSearchParams.entries());

  if (router.currentRoute.path === `${path}`) {
    //  Avoid adding child_app to POV url
    delete queryObj.child_app;
    router.replace({ query: queryObj }).then(() => {
      // Send message to Vue via Angular using existing infrastructure
      outgoingPostMessageUtils.post('update-route-query', { path, query: queryObj });
    }).catch((err) => {
      // Only log non-navigation duplicate errors, as they occur in POV/Angular coordination scenarios
      if (err.name !== 'NavigationDuplicated') {
        logError('Error in continueAngularNavigation was not able to replace the route', err, {
          path,
          queryObj,
          isComingFromAngular,
          isGoingToAngular,
          currentRoute: router.currentRoute.path,
        });
      }
    });
  } else if (isComingFromAngular !== isGoingToAngular) {
    router.push({ path: `${path}`, query: queryObj });
  } else {
    router.replace({ path: `${path}`, query: queryObj });
  }

  store.dispatch('AngularStore/setIsNavigatingToAngular', false);
};

const handleAngularNavigation = async (angularUrl) => {
  const url = new URL(angularUrl);
  const path = url.pathname.substring(url.pathname.indexOf('/app/') + 4);
  const queryString = url.search.replace('?', '');
  const goingTo = router.resolve(path);
  const isGoingToAngular = goingTo.route.meta.isAngular;
  const isCurrentRoutePov = !router.currentRoute.meta.isAngular;
  isAngularEntryRoute = (isCurrentRoutePov && isAngularEntryRoute) || goingTo.route.path === '/entry';
  const isComingFromAngular = !!store.getters['AngularStore/isAngularDisplayed'];

  // Handle navigation between POV and Angular pages

  if (goingTo.route.path !== '/entry') {
    continueAngularNavigation(path, queryString, isComingFromAngular, isGoingToAngular);
  }
};

const enterPovPage = (payload) => {
  handleNavigation(payload);
};

const handleGetDataFromStore = (eventData) => {
  const response = getNestedStoreData(eventData.data.path);
  // Echo back the requestId from the incoming message
  outgoingPostMessageUtils.post('store-data-response', {
    response,
    requestId: eventData.data.requestId,
  });
};

// List getters only — function-style getters can't cross postMessage
const ANGULAR_EXPOSED_APPS_GETTERS = ['purchasedApps'];

const getAppsResponse = async ({
  getter, appType, appCodeName, force,
}) => {
  if (appType && getter) return { error: 'Specify either appType or getter, not both' };

  const isPurchased = getter === 'purchasedApps';
  const loadAction = isPurchased ? 'getSubscribableApps' : 'loadApps';
  await store.dispatch(`AppsStore/${loadAction}`, { force: !!force });

  const { loaded, subscribableAppsLoaded } = store.state.AppsStore;
  if (!loaded || (isPurchased && !subscribableAppsLoaded)) return { error: 'Failed to load apps' };

  if (appType) return { data: store.getters['AppsStore/appsByType'](appType) };

  switch (getter) {
    case undefined:
      return { data: store.state.AppsStore.apps };
    case 'externalAppByCodeName':
      // Angular reads this once, so the owner check needs staff data in first.
      await store.dispatch('StaffStore/fetchStaffData');
      return { data: store.getters['AppsStore/externalAppByCodeName'](appCodeName) ?? null };
    default:
      return ANGULAR_EXPOSED_APPS_GETTERS.includes(getter)
        ? { data: store.getters[`AppsStore/${getter}`] }
        : { error: `Getter not exposed to Angular: ${getter}` };
  }
};

const handleGetAppsFromStore = async (eventData) => {
  const { requestId, ...request } = eventData.data;
  const response = await getAppsResponse(request)
    .catch((error) => ({ error: `Error loading apps: ${error?.message || error}` }));
  outgoingPostMessageUtils.post('store-data-response', { response, requestId });
};

const init = () => {
  window.addEventListener('message', (e) => {
    if (!isSameHostname(e.origin)) return;

    const isAngularDisplayed = store.getters['AngularStore/isAngularDisplayed'];
    const isNavigatingToAngular = store.getters['AngularStore/isNavigatingToAngular'];

    if (typeof e.data !== 'string') {
      switch (e.data.event) {
        case 'update-store':
          /**
           * When updating the store from Angular, we have cases where we want to ignore this, because the page
           * that is trying to make the update is not currently displayed.
           * In other cases, we want to make the update no matter which page is currently being displayed.
           * In that case, the call from Angular should look like this:
           * PovApp.sendMessage('update-store',{type: 'MyStore/setMyData',updateType: 'always',payload: {...myData}})
           */
          // eslint-disable-next-line no-case-declarations
          const angularNavigation = (e.data.data.type === 'AngularStore/setIsNavigatingToAngular' && e.data.data.payload);
          if (e.data.data.updateType === 'always' || isAngularDisplayed || isNavigatingToAngular || angularNavigation) {
            store.dispatch(e.data.data.type, e.data.data.payload);
          }
          break;
        case 'update-quick-actions':
          // TODO: remove this and update the store by using updateType "always"
          // temporary workaround for quick actions until angular dependency is removed
          store.dispatch(e.data.data.type, e.data.data.payload);
          break;
        case 'set-angular-title':
          if (isAngularDisplayed === 1 || isNavigatingToAngular) {
            store.dispatch('LayoutStore/setDocumentTitle', e.data.data.title);
            if (e.data.data.titleEn) {
              store.dispatch('LayoutStore/setDocumentTitleEn', e.data.data.titleEn);
              Vue.prototype.$trackPageName(e.data.data.titleEn);
              markWebVitalsReady(e.data.data.titleEn);
            }
          }
          break;
        case 'track-page-name':
          if (e.data.data?.pageNameEn) {
            Vue.prototype.$trackPageName(e.data.data.pageNameEn);
          }
          break;
        // Angular has changed route, need to update the displayed url
        case 'location-change':
          // TODO: resolve issue of loading directly to route that does not exist in Angular
          // if (isAngularDisplayed || isNavigatingToAngular) {
          handleAngularNavigation(e.data.data.newUrl);
          // } else {
          //   console.log('Angular tried to go to ', e.data.data.newUrl, ' but was rejected ');
          // }
          break;
        case 'pov-navigation':
          enterPovPage(e.data.data);
          break;
        case 'pov-redirect':
          if (isSameOrigin(e.data.data.newUrl)) {
            window.location.href = e.data.data.newUrl;
          }
          break;
        case 'importContacts':
          eventListenerService.emit('importContacts');
          break;
        case 'redirect_to':
          console.log('redirect_to', e.data.data.path);
          window.location.replace(e.data.data.path);
          break;
        case 'setLocale':
          Vue.prototype.setLocale(e.data.data);
          Vue.prototype.addI18nModule('common-pov');
          businessDateFormats.setLocale(e.data.data, null);
          store.dispatch('CommonStore/setIsRtl', e.data.data === 'he');
          break;
        case 'setHeightAsFixed':
          document.getElementsByTagName('body')[0].style.minHeight = 'unset';
          document.getElementsByTagName('body')[0].style.minHeight = `${document.body.scrollHeight}px`;
          break;
        case 'set-fullscreen-mode':
          store.dispatch('AngularStore/setFullscreenState', e.data.data);
          break;
        case 'update-checklist-progress':
          store.dispatch(e.data.data.type, e.data.data.payload);
          break;
        case 'close-modal':
          store.dispatch('ModalStore/closeModal', e.data.data);
          break;
        case 'set-force-modal-mode':
          store.dispatch('ModalStore/setForceModalMode', true);
          break;
        case 'modalStepBack':
          router.go(-2);
          break;
        case 'bizai-event':
          eventListenerService.emit('bizai-event', e.data);
          break;
        case 'bizai':
          eventListenerService.emit('bizai', e.data);
          break;
        case 'open-ai-chat':
          store.dispatch('AiChatStore/openPanel', {
            source: 'dashboard',
            startRecording: e.data.data?.startRecording,
          });
          if (e.data.data?.message) {
            store.dispatch('AiChatStore/sendMessage', {
              text: e.data.data.message,
              context: { page: 'dashboard' },
            });
          }
          break;
        case 'open-help':
          if (helpDestination() === 'chat') {
            openHelp({ message: e.data.data?.message, source: e.data.data?.source });
          }
          break;
        case 'get-data-from-store':
          handleGetDataFromStore(e.data);
          break;
        case 'get-apps-from-store':
          handleGetAppsFromStore(e.data);
          break;
        case 'get-available-locales':
          outgoingPostMessageUtils.post('send-available-locales', store.getters['BusinessStore/availableLocales']);
          break;
        default:
          eventListenerService.emit('on-message', e.data);
      }
    }
  });
};

export default {
  init,
};

export { getNestedStoreData };
